import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Table, Card, Button, Space, Input, Select, Tag } from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  PlayCircleOutlined, 
  FileImageOutlined, 
  VideoCameraOutlined
} from '@ant-design/icons';
import { getAllTools } from '../../graphql/queries';
import { useNavigate } from 'react-router-dom';
import './Studios.css';

const { Option } = Select;

const Tools = () => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [filters, setFilters] = useState({
    imageType: null,
    isFeatured: null,
    templateId: null,
    name: '',
  });

  const { loading, data } = useQuery(getAllTools, {
    variables: {
      filter: {
        imageType: filters.imageType,
        isFeatured: filters.isFeatured,
        templateId: filters.templateId || null,
        name: filters.name || null,
      },
    },
  });

  const handleRowClick = (record) => {
    navigate(`/tools/${record.id}`);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '80px',
    },
    {
      title: 'Banner',
      dataIndex: 'bannerUrl',
      key: 'bannerUrl',
      width: '100px',
      render: (bannerUrl) => bannerUrl ? (
        <img 
          src={bannerUrl} 
          alt="Banner" 
          className="studio-banner"
        />
      ) : '-',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '200px',
    },
    {
      title: 'Type',
      dataIndex: 'imageType',
      key: 'imageType',
      width: '100px',
      render: (imageType) => (
        <Tag color={imageType === 'DUAL' ? 'blue' : 'green'}>
          {imageType}
        </Tag>
      ),
    },
    {
      title: 'Featured',
      dataIndex: 'isFeatured',
      key: 'isFeatured',
      width: '100px',
      render: (isFeatured) => (
        <Tag color={isFeatured ? 'gold' : 'default'}>
          {isFeatured ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: '120px',
      render: (provider) => provider ? (
        <Tag color="blue">
          {provider}
        </Tag>
      ) : '-',
    },
    {
      title: 'Media',
      key: 'media',
      width: '120px',
      render: (_, record) => (
        <Space size="small">
          {record.videoUrl && (
            <Tag color="blue" icon={<PlayCircleOutlined />}>
              Video
            </Tag>
          )}
          {record.webmUrl && (
            <Tag color="purple" icon={<VideoCameraOutlined />}>
              WebM
            </Tag>
          )}
          {record.webpUrl && (
            <Tag color="green" icon={<FileImageOutlined />}>
              WebP
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Cost',
      dataIndex: 'cost',
      key: 'cost',
      width: '80px',
      render: (cost) => cost ? `$${cost}` : '-',
      sorter: (a, b) => (a.cost || 0) - (b.cost || 0),
    },
    {
      title: 'Videos',
      dataIndex: 'videoCount',
      key: 'videoCount',
      width: '80px',
      sorter: (a, b) => a.videoCount - b.videoCount,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '120px',
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
  ];

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    });
  };

  return (
    <div className="studios-container">
      <Card>
        <div className="page-header">
          <div className="header-content">
            <h1>Tools Management</h1>
            <p>Manage and organize your video generation tools</p>
          </div>
          <Button 
            type="primary" 
            size="large"
            icon={<PlusOutlined />}
            onClick={() => navigate('/tools/create')}
          >
            Create New Tool
          </Button>
        </div>

        <div className="filters-section">
          <Space size="large" wrap>
            <div className="filter-item">
              <Input
                placeholder="Search tools by name..."
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
                style={{ width: 250 }}
                prefix={<SearchOutlined />}
                allowClear
              />
            </div>
            <div className="filter-item">
              <Select
                placeholder="Image Type"
                value={filters.imageType}
                onChange={(value) => handleFilterChange('imageType', value)}
                style={{ width: 140 }}
                allowClear
              >
                <Option value="DUAL">DUAL</Option>
                <Option value="SINGLE">SINGLE</Option>
              </Select>
            </div>
            <div className="filter-item">
              <Select
                placeholder="Featured"
                value={filters.isFeatured}
                onChange={(value) => handleFilterChange('isFeatured', value)}
                style={{ width: 120 }}
                allowClear
              >
                <Option value={true}>Featured</Option>
                <Option value={false}>Regular</Option>
              </Select>
            </div>
            <div className="filter-item">
              <Input
                placeholder="Template ID"
                value={filters.templateId}
                onChange={(e) => handleFilterChange('templateId', e.target.value)}
                style={{ width: 130 }}
                allowClear
              />
            </div>
          </Space>
        </div>
        
        <Table
          columns={columns}
          dataSource={data?.getAllTools || []}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} tools`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' }
          })}
        />
      </Card>
    </div>
  );
};

export default Tools; 